import { createBrowserClient } from '@supabase/ssr';
import type { SupabaseClient } from '@supabase/supabase-js';

import { Database } from '../database.types';
import { getSupabaseClientKeys } from '../get-supabase-client-keys';

/**
 * @name getSupabaseBrowserClient
 * @description Get a Supabase client for use in the Browser
 */
export function getSupabaseBrowserClient<GenericSchema = Database>() {
  const keys = getSupabaseClientKeys();
  const client = createBrowserClient<GenericSchema>(keys.url, keys.publicKey);

  pinRealtimeToUserToken(client);

  return client;
}

/**
 * Realtime re-reads its token on every heartbeat (25 s) and pushes any change
 * to every joined channel. supabase-js's default callback falls back to the
 * publishable key whenever getSession() yields no session, which happens
 * without a SIGNED_OUT event when a refresh fails with a network error or a
 * server response clears the auth cookie. Channels re-authed that way run as
 * `anon`, which has no column grants on the published tables, so Postgres
 * logs "invalid column for filter account_id" on every rejoin.
 *
 * Throwing instead makes RealtimeClient keep its last user token; the next
 * TOKEN_REFRESHED/SIGNED_IN event replaces it. A getSession() error is a
 * transient refresh failure, so the channels stay up. No session and no error
 * means the session is gone (SIGNED_OUT, or the cookie was cleared without an
 * event in this tab), so the channels are closed rather than left streaming
 * on the cached token until it expires. Every realtime subscriber in the app
 * requires a signed-in user.
 */
function pinRealtimeToUserToken(
  client: Pick<SupabaseClient, 'auth' | 'realtime' | 'removeAllChannels'>,
) {
  client.realtime.accessToken = async () => {
    const { data, error } = await client.auth.getSession();

    if (data.session) {
      return data.session.access_token;
    }

    if (!error) {
      void client.removeAllChannels();
    }

    throw error ?? new Error('No auth session for realtime');
  };
}
